import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Add, Check, Close, DeleteOutline, EditOutlined, Groups } from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import DepartmentDetailDrawer from 'components/admin-organizations/DepartmentDetailDrawer';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import {
  ORGANIZATION_DEPARTMENTS,
  CREATE_ORGANIZATION_DEPARTMENT,
  UPDATE_ORGANIZATION_DEPARTMENT,
  DELETE_ORGANIZATION_DEPARTMENTS
} from 'api/graphql/wellbeing';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Bienestar Corporativo (admin) — departamentos (áreas) de la organización: crear inline,
 * renombrar, activar/ocultar y borrar (los miembros quedan "sin departamento"). Estos
 * grupos alimentan el heatmap de bienestar y el filtro de miembros.
 */
function OrganizationDepartmentsTab({ organizationId }) {
  const { t } = useTranslation(['organizations']);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // { id, name }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailDepartment, setDetailDepartment] = useState(null);

  const { data, loading, refetch } = useQuery(ORGANIZATION_DEPARTMENTS, {
    variables: { id: organizationId },
    fetchPolicy: 'cache-and-network'
  });

  const refetchQueries = [{ query: ORGANIZATION_DEPARTMENTS, variables: { id: organizationId } }];
  const [createDepartment, { loading: creating }] = useMutation(CREATE_ORGANIZATION_DEPARTMENT, { refetchQueries });
  const [updateDepartment] = useMutation(UPDATE_ORGANIZATION_DEPARTMENT, { refetchQueries });
  const [deleteDepartments] = useMutation(DELETE_ORGANIZATION_DEPARTMENTS, { refetchQueries });

  const departments = data?.organization?.departments || [];

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await createDepartment({ variables: { organization_id: organizationId, name } });
      setNewName('');
    } catch {
      // El errorHandler global muestra el toast ("Ya existe un departamento...").
    }
  };

  const handleRename = async () => {
    const name = editing?.name?.trim();
    if (!editing || !name) return;
    try {
      await updateDepartment({ variables: { id: editing.id, name } });
      setEditing(null);
    } catch {
      // Toast global.
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    await deleteDepartments({ variables: { id: [target.id] } });
    refetch();
  };

  return (
    <Box>
      {/* Crear inline */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          label={t('newDepartment', { defaultValue: 'Nuevo departamento (área)' })}
          placeholder={t('departmentPlaceholder', { defaultValue: 'Ej. Tecnología, Ventas, RRHH' })}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        {/* El CTA acompaña a un input `size="small"`: va a su MISMA altura (40px) y ancho amplio,
            no como una píldora corta y alta al lado del campo. */}
        <Button
          startIcon={<Add />}
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          sx={{ ...premiumButtonSx, flex: 'none', py: 0, height: 40, borderRadius: 2, fontSize: '0.9rem', width: { xs: '100%', sm: 220 } }}>
          {t('createDepartment', { defaultValue: 'Crear' })}
        </Button>
      </Stack>

      {loading && departments.length === 0 && <Skeleton variant="rounded" height={160} sx={{ borderRadius: '14px' }} />}

      {!loading && departments.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'hairline.main', borderRadius: 3, color: 'text.secondary' }}>
          <Groups sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2" fontWeight={600}>
            {t('departmentsEmpty', { defaultValue: 'Aún no hay departamentos. Créalos aquí o impórtalos con la columna "área" del Excel.' })}
          </Typography>
        </Box>
      )}

      {departments.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.25 }}>
          {t('departmentsHint', { defaultValue: 'Abre un departamento para ver cuánto usa la app su gente.' })}
        </Typography>
      )}

      <Stack spacing={1.25}>
        {departments.map((department) => {
          const isEditing = editing?.id === department.id;
          const openDetail = () => setDetailDepartment({ id: department.id, name: department.name });

          return (
            <Stack
              key={department.id}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 1.75,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.content',
                transition: `transform 200ms ${EASE}, border-color 200ms ${EASE}`,
                '&:hover': { borderColor: isEditing ? 'hairline.main' : 'hairline.strong' },
                '&:active': { transform: isEditing ? 'none' : 'scale(0.98)' }
              }}>
              {isEditing ? (
                <>
                  <TextField
                    size="small"
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                    inputProps={{ 'aria-label': t('rename', { defaultValue: 'Renombrar' }) }}
                    sx={{ flex: 1, maxWidth: 360 }}
                  />
                  <IconButton size="small" onClick={handleRename} aria-label={t('save', { defaultValue: 'Guardar' })} sx={{ color: 'gold.main' }}>
                    <Check sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditing(null)} aria-label={t('cancel', { defaultValue: 'Cancelar' })} sx={{ color: 'text.secondary' }}>
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              ) : (
                <>
                  {/* El área clicable excluye a propósito los íconos de acción: un `role="button"`
                      que envuelva otros botones no se anuncia bien en lectores de pantalla. */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    role="button"
                    tabIndex={0}
                    aria-label={t('viewDepartmentDetail', {
                      name: department.name,
                      defaultValue: 'Ver el detalle de uso de {{name}}'
                    })}
                    onClick={openDetail}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetail();
                      }
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      py: 0.5,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      '&:focus-visible': { outline: '2px solid var(--otx-focus-ring)', outlineOffset: 2 }
                    }}>
                    <Typography variant="body2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {department.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('departmentMembers', { count: department.members_count, defaultValue: '{{count}} miembros' })}
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'surface.overlay', border: '1px solid', borderColor: 'hairline.main' }}
                    />
                    {!department.is_active && (
                      <Chip size="small" label={t('inactive', { defaultValue: 'Inactivo' })} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'surface.overlay', color: 'text.secondary' }} />
                    )}
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => setEditing({ id: department.id, name: department.name })}
                    aria-label={t('rename', { defaultValue: 'Renombrar' })}
                    sx={{ color: 'text.secondary' }}>
                    <EditOutlined sx={{ fontSize: 17 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(department)}
                    aria-label={t('deleteDepartment', { defaultValue: 'Eliminar departamento' })}
                    sx={{ color: 'sessionStatus.absent.fg' }}>
                    <DeleteOutline sx={{ fontSize: 17 }} />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        })}
      </Stack>

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('deleteDepartmentTitle', { defaultValue: 'Eliminar departamento' })}
        message={t('deleteDepartmentMessage', {
          name: deleteTarget?.name || '',
          defaultValue: '¿Eliminar "{{name}}"? Sus miembros quedarán sin departamento (no se borran).'
        })}
      />

      <DepartmentDetailDrawer
        open={Boolean(detailDepartment)}
        onClose={() => setDetailDepartment(null)}
        organizationId={organizationId}
        department={detailDepartment}
      />
    </Box>
  );
}

OrganizationDepartmentsTab.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default OrganizationDepartmentsTab;
